/**
 * The navigation tree: one tree for every persona, decided by what the API answers.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE RULE (privacy as API absence)
 *   A surface the server withholds from this principal is ABSENT from the tree, never
 *   greyed out: zero role branches are rendered for it. The one conditional entry
 *   (configuration) appears only on the configuration probe's answer, which is the
 *   server's, not this module's opinion about identity.
 *
 *   Entries that name a station a later milestone delivers (the calendar grid, the
 *   lessons-learned register, the evaluation view) are present because their absence
 *   would be a lie of a different kind: the direction document names them "future",
 *   never broken, and their routes render that future state deliberately.
 */

import { strings } from "../brand/strings";

/** One navigation leaf. `to` is the route the entry deep-links to. */
export interface NavEntry {
  readonly label: string;
  readonly to: string;
  /** Rendered only when this answer from the API is true. Absent means always. */
  readonly when?: "configurationProbe" | "auditProbe";
}

/** One navigation group, rendered in the sidebar's own order. */
export interface NavGroup {
  readonly heading: string;
  readonly entries: readonly NavEntry[];
}

export const PROGRAM_ROUTES = {
  engineering: "/programs/engineering",
  procurement: "/programs/procurement",
  construction: "/programs/construction",
  externalAudits: "/programs/external-audits",
} as const;

/** The canonical sidebar, in the direction document's order (section 1, M4 column). */
export function navigationTree(): NavGroup[] {
  return [
    {
      heading: strings.nav.groups.overview,
      entries: [
        { label: strings.nav.dashboard, to: "/dashboard" },
        { label: strings.nav.calendar, to: "/calendar" },
      ],
    },
    {
      heading: strings.nav.groups.programs,
      entries: [
        { label: strings.nav.programEngineering, to: PROGRAM_ROUTES.engineering },
        { label: strings.nav.programProcurement, to: PROGRAM_ROUTES.procurement },
        { label: strings.nav.programConstruction, to: PROGRAM_ROUTES.construction },
        { label: strings.nav.programExternalAudits, to: PROGRAM_ROUTES.externalAudits },
      ],
    },
    {
      heading: strings.nav.groups.registers,
      entries: [
        { label: strings.nav.itps, to: "/itps" },
        { label: strings.nav.inspectionsRfis, to: "/inspections" },
        { label: strings.nav.rfis, to: "/rfis" },
        { label: strings.nav.findingsPunch, to: "/findings" },
        { label: strings.nav.nonConformities, to: "/actions" },
        { label: strings.nav.schedules, to: "/schedules" },
        { label: strings.nav.standards, to: "/standards" },
        { label: strings.nav.credentials, to: "/credentials" },
      ],
    },
    {
      heading: strings.nav.groups.portfolio,
      entries: [
        { label: strings.nav.projectsContracts, to: "/portfolio/projects" },
        { label: strings.nav.partiesContractors, to: "/portfolio/parties" },
      ],
    },
    {
      heading: strings.nav.groups.intelligence,
      entries: [
        { label: strings.nav.lessonsLearned, to: "/lessons" },
        { label: strings.nav.myEvaluations, to: "/evaluations" },
      ],
    },
    {
      heading: strings.nav.groups.workspace,
      entries: [
        { label: strings.nav.configuration, to: "/configuration", when: "configurationProbe" },
        // Wave AUD frontend item 2: the audit register. The route answers a principal holding
        // AUDIT_LOG READ only (404 to anyone else); the entry rides the audit probe.
        { label: strings.nav.audit, to: "/audit", when: "auditProbe" },
        { label: strings.nav.guide, to: "/guide" },
        { label: strings.nav.account, to: "/account" },
        // Wave b12 frontend item 4: the outboxes. The route answers the tenant's power user
        // only (404 to anyone else); the entry rides the configuration probe, the
        // administrative capability the client can ask the server about.
        { label: strings.nav.outbox, to: "/outbox", when: "configurationProbe" },
      ],
    },
  ];
}
